import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { resetState as resetFormState } from './form.slice'
import { resetState as resetSubmissionState } from './submission.slice'
import { AuthService } from '../services/auth.service'
import { UserService } from '../services/user.service'
import {
  ClientAuthDto,
  LoginDto,
  NewUserDto,
  UpdateAuthUserDto,
  UserSchema
} from '../utils/types/auth'

const initialState: {
  isLoading: boolean
  data: UserSchema | null
  error: string
} = {
  isLoading: false,
  data: null,
  error: ''
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setAuthState: (state, data) => {
      state.data = data.payload
    },
    addForm: (state, data) => {
      if (state.data) {
        state.data.forms = [data.payload, ...state.data.forms]
      }
    },
    removeForm: (state, data) => {
      const formId = data.payload

      if (state.data) {
        const formIndex = state.data.forms.findIndex((id) => id === formId)
        state.data.forms.splice(formIndex, 1)
      }
    },
    removeNotification: (state, data) => {
      /* if (state.data) {
        state.data.notifications = data.payload
      } */
    },
    resetState: (state) => {
      state.isLoading = false
      state.data = null
      state.error = ''
    }
  },
  extraReducers: (builder) => {
    builder.addCase(setAuthSession.fulfilled, (state, action) => {
      state.data = action.payload ?? null
    })
    builder.addCase(login.fulfilled, (state, action) => {
      state.data = action.payload.authUser
      if (state.error) state.error = ''
    })
    builder.addCase(fetchAuthUser.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(fetchAuthUser.fulfilled, (state, action) => {
      state.isLoading = false
      state.data = action.payload
      if (state.error) state.error = ''
    })
    builder.addCase(fetchAuthUser.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.error.message || 'Error authenticating user'
    })
    builder.addCase(updateAuthProfile.fulfilled, (state, action) => {
      state.data = action.payload.authUser
    })
  }
})

export const signup = createAsyncThunk('auth/signup', async (user: NewUserDto) => {
  try {
    const { status } = await AuthService.signup(user)
    return status
  } catch (error) {
    return Promise.reject(error)
  }
})

export const setAuthSession = createAsyncThunk(
  'auth/set-session',
  async (clientAuth: ClientAuthDto) => {
    try {
      const { data: authUser } = await AuthService.setSession(clientAuth)
      localStorage.setItem('uid', authUser._id)
      return authUser
    } catch (error) {
      return Promise.reject(error)
    }
  }
)

export const login = createAsyncThunk('auth/login', async (user: LoginDto) => {
  try {
    const { data: authUser, status } = await AuthService.login(user)
    localStorage.setItem('uid', authUser._id)
    return { authUser, status }
  } catch (error: any) {
    const errorStr = JSON.stringify(error)
    return Promise.reject(errorStr)
  }
})

export const logout = createAsyncThunk('auth/logout', async (_param, { dispatch }) => {
  try {
    const { status } = await AuthService.logout()

    if (status === 200) {
      resetAppState(dispatch)
    }

    return { status }
  } catch (error: any) {
    return Promise.reject(error)
  }
})

export const recoverAccount = createAsyncThunk('auth/recover-account', async (email: string) => {
  try {
    const { status } = await AuthService.recoverAccount(email)
    return { status }
  } catch (error: any) {
    return Promise.reject(error)
  }
})

export const fetchAuthUser = createAsyncThunk('users/fetch-auth', async (uid: string) => {
  try {
    const { data } = await UserService.getOne(uid)
    return data
  } catch (error) {
    return Promise.reject(error)
  }
})

export const updatePassword = createAsyncThunk(
  'auth/update-password',
  async (payload: { password: string; clientAuth?: ClientAuthDto }) => {
    try {
      const { data, status } = await AuthService.updatePassword(payload)
      return { data, status }
    } catch (error: any) {
      return Promise.reject(error)
    }
  }
)

export const updateAuthProfile = createAsyncThunk(
  'auth/update-user-profile',
  async (payload: { id: string; user: UpdateAuthUserDto }) => {
    try {
      const { data: authUser, status } = await UserService.update(payload)
      return { authUser, status }
    } catch (error: any) {
      return Promise.reject(error)
    }
  }
)

export const deleteAccount = createAsyncThunk(
  'auth/delete-account',
  async (id: string, { dispatch }) => {
    try {
      const { status } = await AuthService.deleteAccount(id)

      if (status === 200) {
        resetAppState(dispatch)
      }

      return { status }
    } catch (error) {
      return Promise.reject(error)
    }
  }
)

export const { reducer: authReducer } = authSlice
export const { addForm, removeForm, setAuthState, removeNotification } = authSlice.actions

const resetAppState = (dispatch: Function) => {
  dispatch(resetSubmissionState())
  dispatch(resetFormState())
  dispatch(authSlice.actions.resetState())
}
